import { FC, Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  TextField,
  formattedCurrency,
  ScheduledPaymentUpcomingTransaction,
  SupportedCurrencies,
  withStyles,
} from 'vopay';
import { Title, BodyText, CircleButton, InfoBanner, Button } from 'UI';
import styles from './EditBulkAmountDialogue.module.scss';

const cn = withStyles(styles);

type AmountGroup = {
  amount: string;
  count: number;
};

export type EditBulkAmountDialogueProps = {
  upcomingTransactions: ScheduledPaymentUpcomingTransaction[];
  currency: SupportedCurrencies;
  onCancel: () => void;
  onConfirm: (newAmount: number) => void;
};

const EditBulkAmountDialogue: FC<EditBulkAmountDialogueProps> = ({
  upcomingTransactions,
  currency,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'ScheduledTransactions.Details.EditBulkAmountDialogue' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const [newAmount, setNewAmount] = useState('');

  const amountGroups: AmountGroup[] = useMemo(() => {
    const counts = new Map<string, number>();
    upcomingTransactions.forEach((transaction) => {
      counts.set(transaction.Amount, (counts.get(transaction.Amount) || 0) + 1);
    });

    return Array.from(counts.entries()).map(([amount, count]) => ({ amount, count }));
  }, [upcomingTransactions]);

  const hasMultipleAmounts = amountGroups.length > 1;
  const isConfirmDisabled = newAmount === '' || Number(newAmount) <= 0;

  return (
    <Card className={cn('container')} hoverEffect={false}>
      <div className={cn('header')}>
        <Title level="4">{t('title', { defaultValue: 'Edit Bulk Amount(s)' })}</Title>
        <BodyText className={cn('description')}>
          {t('description', { defaultValue: 'Change amount(s) of all the remaining scheduled transactions.' })}
        </BodyText>

        <div className={cn('closeButton')}>
          <CircleButton
            icon="close"
            size="medium"
            circleFill
            aria-label={tGlobal('Buttons.close')}
            onClick={onCancel}
          />
        </div>
      </div>

      <div className={cn('body')}>
        <div className={cn('section')}>
          <div className={cn('amounts')}>
            <div className={cn('columnHeader')}>{t('Columns.amount', { defaultValue: 'Amount' })}</div>
            <div className={cn('columnHeader')}>
              {t('Columns.remainingTransactions', { defaultValue: 'Remaining Transactions' })}
            </div>

            {amountGroups.map((group) => (
              <Fragment key={group.amount}>
                <div className={cn('cell')}>{`${currency} ${formattedCurrency(group.amount)}`}</div>
                <div className={cn('cell')}>{group.count}</div>
              </Fragment>
            ))}
          </div>

          {hasMultipleAmounts && (
            <InfoBanner>
              {t('replaceAllAmountsInfo', {
                defaultValue: 'The new amount below will replace all the different amounts shown above.',
              })}
            </InfoBanner>
          )}

          <Box flx="col" gap="sm">
            <BodyText weight="md">{t('newAmountLabel', { defaultValue: 'New Amount' })}</BodyText>
            <TextField
              name="new-amount"
              variant="outlineShadow"
              placeholder="00.00"
              preset="number"
              width="100%"
              value={newAmount}
              onChange={(e) => setNewAmount(e.target.value)}
            />
          </Box>
        </div>
      </div>

      <div className={cn('footer')}>
        <Button className={cn('footerButton')} size="small" colorScheme="secondary" onClick={onCancel}>
          {tGlobal('Buttons.cancel')}
        </Button>
        <Button
          className={cn('footerButton')}
          size="small"
          colorScheme="primary"
          disabled={isConfirmDisabled}
          onClick={() => onConfirm(Number(newAmount))}
        >
          {tGlobal('Buttons.confirm')}
        </Button>
      </div>
    </Card>
  );
};

export default EditBulkAmountDialogue;
